import classNames from "classnames";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    buildCalendarHeatmapWeeks,
    CalendarHeatmapDatum,
    getDefaultHeatmapCellLabel,
    getHeatmapIntensityLevel,
} from "./CalendarHeatmap.lib";
import styles from "./CalendarHeatmap.module.scss";

interface CalendarHeatmapProps {
    data: readonly CalendarHeatmapDatum[];
    startDate: string;
    endDate: string;
    ariaLabel: string;
    getCellLabel?: (cell: CalendarHeatmapDatum) => string;
    onCellClick?: (cell: CalendarHeatmapDatum) => void;
    className?: string;
}

export const CalendarHeatmap = ({
    data,
    startDate,
    endDate,
    ariaLabel,
    getCellLabel = getDefaultHeatmapCellLabel,
    onCellClick,
    className,
}: CalendarHeatmapProps) => {
    const weeks = buildCalendarHeatmapWeeks({ startDate, endDate, data });
    const maxValue = data.reduce(
        (currentMax, item) => Math.max(currentMax, item.value),
        0,
    );

    return (
        <div
            className={classNames(styles.root, className)}
            role="group"
            aria-label={ariaLabel}
        >
            <div className={styles.weeks}>
                {weeks.map((week) => (
                    <div
                        className={styles.week}
                        data-week-start={week.cells[0]?.date}
                        key={week.cells[0]?.date}
                    >
                        {week.cells.map((cell) => {
                            if (cell.isPlaceholder) {
                                return (
                                    <div
                                        className={styles.placeholder}
                                        data-placeholder-date={cell.date}
                                        key={cell.date}
                                        aria-hidden
                                    />
                                );
                            }

                            const label = getCellLabel({
                                date: cell.date,
                                value: cell.value,
                            });
                            const level = getHeatmapIntensityLevel(
                                cell.value,
                                maxValue,
                            );

                            return (
                                <Tooltip key={cell.date} content={label}>
                                    <button
                                        type="button"
                                        className={styles.cell}
                                        data-date={cell.date}
                                        data-level={level}
                                        aria-label={label}
                                        onClick={() =>
                                            onCellClick?.({
                                                date: cell.date,
                                                value: cell.value,
                                            })
                                        }
                                    />
                                </Tooltip>
                            );
                        })}
                    </div>
                ))}
            </div>
            <div className={styles.months} aria-hidden>
                {weeks.map((week) => (
                    <span className={styles.month} key={week.cells[0]?.date}>
                        {week.monthLabel ?? ""}
                    </span>
                ))}
            </div>
        </div>
    );
};
